import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom"; // React Router is required for query params
import { marked } from "marked";
import hljs from "highlight.js";
import "highlight.js/styles/github.css"; // Use the GitHub theme for better contrast
import "../css/ChatDesktop.css";
import mic from "../assets/mic.png";
import send from "../assets/send.png";
import avatar from "../assets/avatar.png";
import dropdown from "../assets/dropdown.png";
import dropup from "../assets/dropup.png";
import tick from "../assets/tick.png";

const ChatDesktop = () => {
  const [isModelToolkitDropdownActive, setIsModelToolkitDropdownActive] = useState(false); // Model/Toolkit dropdown
  const [type, setType] = useState("basic"); // Default to "basic"
  const [searchParams, setSearchParams] = useSearchParams();
  const toolOptions = ['Google', 'Yahoo Finance', 'CoinGecko', 'Arxiv'];
  const [selectedTools, setSelectedTools] = useState(['Google', 'CoinGecko']); // State to track selected tools in agentic mode
  const [selectedModel, setSelectedModel] = useState('Meta-Llama-3.2-3B-Instruct');
  const [isAutoScrolling, setIsAutoScrolling] = useState(true); // Track auto-scrolling
  const chatWindowRef = useRef(null); // Ref for the chat window container
  const lastUserMessageRef = useRef(null); // Ref for the last user message

  const modelOptions = [
    { name: 'Meta-Llama-3.2-3B-Instruct', description: 'Optimized for edge devices', available: true },
    { name: 'Meta-Llama-3.3-70B-Instruct', description: 'Advanced insights for complex questions', available: true },
    { name: 'Llama-4-Maverick-17B-128E-Instruct', description: 'Great context-awareness at unparalleled speeds', available: true },
    { name: 'Llama-4-Scout-17B-16E-Instruct', description: 'Great context-awareness at unparalleled speeds', available: true },
    { name: 'DeepSeek-V3-0324', description: 'Great context-awareness at unparalleled speeds', available: true },
  ];
  const [chatHistory, setChatHistory] = useState([]); // Chat history state
  const [currentMessage, setCurrentMessage] = useState(""); // Current message state
  const textareaRef = useRef(null); // Create a ref for the textarea
  const dropdownRef = useRef(null); // Ref for the dropdown container
  const renderFormattedMessage = (message) => {
    if (!message) return { __html: "" };

    // Use `marked` to parse Markdown
    const htmlContent = marked(message, {
      breaks: true,
      gfm: true,
      highlight: (code, lang) => {
        if (lang && hljs.getLanguage(lang)) {
          return hljs.highlight(code, { language: lang }).value;
        }
        return hljs.highlightAuto(code).value; // Auto-detect language
      },
    });

    return { __html: htmlContent };
  };

  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null); // Reference for SpeechRecognition

  const handleVoiceInput = () => {
    // Check if SpeechRecognition is supported
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    // Initialize SpeechRecognition if it doesn't already exist
    if (!recognitionRef.current) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      // Set up SpeechRecognition events
      recognitionRef.current.onstart = () => {
        setIsRecording(true);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false); // Stop recording when recognition ends
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setCurrentMessage((prev) => `${prev} ${transcript}`); // Append recognized text to input
      };

      // Handle errors and auto-stop
      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        recognitionRef.current.stop();
      };
    }

    // Toggle recognition start/stop based on `isRecording`
    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };

  // Scroll Detection Logic
  const handleScroll = () => {
    const chatWindow = chatWindowRef.current; // chat-history reference
    if (!chatWindow) return;

    // Determine if the user is near the bottom
    const isAtBottom =
      Math.abs(chatWindow.scrollHeight - chatWindow.scrollTop - chatWindow.clientHeight) < 1; // Allow slight rounding differences
    setIsAutoScrolling(isAtBottom);
  };


  // Auto-Scroll to the latest message when `isAutoScrolling` is true
  useEffect(() => {
    if (isAutoScrolling && lastUserMessageRef.current) {
      lastUserMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  // Attach `handleScroll` to the scroll event
  // Attach `handleScroll` to the scroll event
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (chatWindow) {
      chatWindow.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatWindow) {
        chatWindow.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Prevent scroll propagation to the parent page or iframe
  useEffect(() => {
    const chatWindow = chatWindowRef.current;
    if (chatWindow) {
      const preventScrollPropagation = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = chatWindow;
        if (
          (scrollTop === 0 && e.deltaY < 0) || // At the top and trying to scroll up
          (scrollTop + clientHeight === scrollHeight && e.deltaY > 0) // At the bottom and trying to scroll down
        ) {
          e.preventDefault(); // Prevent scroll propagation
        }
      };

      chatWindow.addEventListener("wheel", preventScrollPropagation);

      return () => {
        chatWindow.removeEventListener("wheel", preventScrollPropagation);
      };
    }
  }, []);


  const handleExportChat = () => {
    // Create a blob from the chat history
    const blob = new Blob([JSON.stringify(chatHistory, null, 2)], {
      type: 'application/json',
    });

    // Create a temporary URL for the file
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chat_history.json';
    a.click();

    // Clean up the URL
    URL.revokeObjectURL(url);
  };

  const handleImportChat = (e) => {
    const file = e.target.files[0]; // Get the selected file
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Parse the JSON content
        const importedHistory = JSON.parse(e.target.result);

        // Validate the imported data structure
        if (
          Array.isArray(importedHistory) && // Ensure it's an array
          importedHistory.every(
            (msg) => typeof msg === "object" && msg.role && msg.content
          ) // Ensure each item has `role` and `content`
        ) {
          setChatHistory(importedHistory); // Update the chat history
        } else {
          // Invalid structure
          throw new Error("Invalid chat history format.");
        }
      } catch (error) {
        console.error("Error importing chat history:", error);
        alert(
          "Failed to import chat history. Please ensure the file is a valid JSON with the correct format."
        );
      }
    };

    reader.readAsText(file); // Read the file as text
  };




  // Handle query parameters and set the active type
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type') || 'basic'; // Default to 'basic' if no query param
    setType(type); // Initialize chat type based on URL
    console.log(type)
  }, []);

  const handleTypeToggle = (newType) => {
    setType(newType);
    setSearchParams({ type: newType }); // Update query parameter
  };

  const handleModelToolkitToggle = () => {
    setIsModelToolkitDropdownActive((prev) => !prev); // Toggle Model/Toolkit dropdown
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) // Check if click is outside the dropdown
      ) {
        setIsModelToolkitDropdownActive(false); // Close the dropdown
      }
    };

    // Attach the event listener
    document.addEventListener("click", handleOutsideClick);

    // Cleanup on component unmount
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []); // Empty dependency array to run this effect only once

  const handleSubmit = async () => {
    if (!currentMessage.trim()) return;

    // Add the user's message to the chat history
    const newChatHistory = [
      ...chatHistory,
      { role: "user", content: currentMessage },
    ];
    setChatHistory(newChatHistory);

    // Add a placeholder for the assistant's loading animation
    setChatHistory((prev) => [
      ...prev,
      { role: "assistant", content: "..." }, // Loading animation placeholder
    ]);

    // Reset the message input and textarea height
    setCurrentMessage("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "60px";
    }

    // Prepare the data for the request
    // Limit to the last 4 messages
    const limitedChatHistory = newChatHistory.slice(-4);

    // Check if the last message is a duplicate of the current user input
    if (
      limitedChatHistory.length > 0 &&
      limitedChatHistory[limitedChatHistory.length - 1].content === currentMessage &&
      limitedChatHistory[limitedChatHistory.length - 1].role === "user"
    ) {
      // Remove the last message if it's a duplicate user message
      limitedChatHistory.pop();
    }

    const data = {
      user_input: currentMessage,
      model_name: selectedModel,
      chat_history: limitedChatHistory,
      ...(type === "agentic" && { tools: selectedTools }), // Include tools in agentic mode
    };

    // Determine the API route based on the chat type
    const route = type === "agentic" ? "/agentic" : "/basic";

    try {
      // Make the API request
      const response = await fetch(
        `https://avachatbe.ai.aitech.io${route}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      // Process the response
      if (type === "agentic") {
        const fullResponse = await response.text();
        setChatHistory((prev) => {
          const updatedHistory = [...prev];
          updatedHistory[updatedHistory.length - 1] = {
            role: "assistant",
            content: fullResponse,
          };
          return updatedHistory;
        });
      } else {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let done = false;
        let fullResponse = "";
        let buffer = "";

        while (!done) {
          const { done: readDone, value } = await reader.read();
          done = readDone;
          if (value) {
            buffer += decoder.decode(value, { stream: true });

            try {
              const jsonObjects = buffer.split("\n").filter(Boolean);
              for (const jsonObj of jsonObjects) {
                const jsonChunk = JSON.parse(jsonObj);
                const responseChunk = jsonChunk.chunk;
                fullResponse += responseChunk;

                setChatHistory((prev) => {
                  const updatedHistory = [...prev];
                  const lastMessage =
                    updatedHistory[updatedHistory.length - 1];
                  updatedHistory[updatedHistory.length - 1] = {
                    ...lastMessage,
                    content: fullResponse,
                  };
                  return updatedHistory;
                });
              }
              buffer = "";
            } catch {
              console.log(
                "Waiting for more chunks to form a complete JSON object"
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error during request:", error);

      // Update the assistant's message box with the error message
      setChatHistory((prev) => {
        const updatedHistory = [...prev];
        updatedHistory[updatedHistory.length - 1] = {
          role: "assistant",
          content: `An error occurred: ${error.message}`,
        };
        return updatedHistory;
      });
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent the default action (line break)
      handleSubmit();
    }
  };


  return (
    <div className="chat-desktop-container">
      <div className="chat-header">
        <div className="chat-header-container">
          <div className="chat-options">
            <img src={avatar} alt="Avatar" className="chat-desktop-avatar" />
            <div className="chat-desktop-title">
              <span className="boldText">AVA</span>
              <span className="normalText">CHAT</span>
            </div>
          </div>
          <div className="chat-options">
            {/* Toggle Between Basic and Agentic */}
            <div className="desktop-toggle-container">
              <div
                className={`desktop-toggle-option ${type === "basic" ? "active" : ""}`}
                onClick={() => handleTypeToggle("basic")}
              >
                Basic
              </div>
              <div
                className={`desktop-toggle-option ${type === "agentic" ? "active" : ""}`}
                onClick={() => handleTypeToggle("agentic")}
              >
                Agentic
              </div>
            </div>

            {/* Model/Toolkit Dropdown */}
            <div
              ref={dropdownRef} // Attach ref to the dropdown container
              className="desktop-dropdown-container"
              onClick={() => setIsModelToolkitDropdownActive((prev) => !prev)}
            >
              <div className="desktop-dropdown-header">
                {type === "basic" ? selectedModel : "Toolkit"}
                {isModelToolkitDropdownActive ? <img src={dropup} alt="dropup" className="navbar-icon" /> : <img src={dropdown} alt="dropdown" className="navbar-icon" />}
              </div>
              {isModelToolkitDropdownActive && (
                <div className="desktop-dropdown-options">
                  {type === "basic" &&
                    modelOptions.map((model) => (
                      <div
                        key={model.name}
                        className={`desktop-dropdown-option ${selectedModel === model.name ? "selected" : ""
                          }`}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent event bubbling
                          setSelectedModel(model.name); // Select the model
                          setIsModelToolkitDropdownActive(false); // Close the dropdown
                        }}
                      >
                        {model.name}
                      </div>
                    ))}

                  {type === "agentic" &&
                    toolOptions.map((tool) => (
                      <div
                        key={tool}
                        className={`dropdown-option ${selectedTools.includes(tool) ? "selected" : ""
                          }`}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent event bubbling
                          setSelectedTools((prev) =>
                            prev.includes(tool)
                              ? prev.filter((t) => t !== tool) // Remove if already selected
                              : [...prev, tool] // Add if not selected
                          );
                        }}
                      >
                        {tool}
                        {selectedTools.includes(tool) && (
                          <div className="checkmark">
                            <img src={tick} alt="tick" className="navbar-icon" />
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`chat-body ${chatHistory.length === 0 ? "empty" : "populated"}`}>
        {chatHistory.length === 0 ? (
          <div className="start-message">Start chatting with Ava now!</div>
        ) : (
          <div className="chat-history" ref={chatWindowRef}>
            {chatHistory.map((message, index) => (
              <div
                key={index}
                className={`desktop-chat-message ${message.role === "user" ? "desktop-user-message" : "desktop-assistant-message"
                  }`}
                ref={index === chatHistory.length - 1 ? lastUserMessageRef : null} // Assign ref to the last message
              >
                {message.content === "..." ? (
                  <div className="dot-loading">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                ) : message.role === "assistant" ? (
                  <div
                    className="formatted-message"
                    dangerouslySetInnerHTML={renderFormattedMessage(message.content)}
                  />
                ) : (
                  message.content
                )}
              </div>
            ))}

          </div>

        )}
      </div>

      <div className="chat-footer">
        <footer className="chat-desktop-footer">
          <button
            className={`footer-icon-button ${isRecording ? "recording" : ""}`} // Add a "recording" class if recording
          // onClick={handleVoiceInput}
          >
            <img src={mic} alt="Mic" className="footer-icon" onClick={handleVoiceInput} />
          </button>

          <div>
            {/* Container for textarea */}
            <div className="desktop-chat-input-container">
              <textarea
                ref={textareaRef}
                placeholder={isRecording ? "Listening..." : "Ask me anything..."} // Dynamic placeholder
                className="desktop-chat-input"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                onInput={(e) => {
                  e.target.style.height = "auto"; // Reset the height
                  e.target.style.height = `${e.target.scrollHeight - 30}px`; // Adjust dynamically
                }}
                disabled={isRecording} // Disable when isRecording is true
              ></textarea>

            </div>

            {/* Conditionally show Import and Export buttons */}
            {chatHistory.length > 0 && (
              <div className="chat-action-buttons">
                <label htmlFor="importChat" className="action-button">
                  Import Chat
                </label>
                <input
                  type="file"
                  id="importChat"
                  accept="application/json"
                  onChange={handleImportChat}
                  className="fileInput"
                  style={{ display: 'none' }}
                />

                <button className="action-button" onClick={handleExportChat}>
                  Export Chat
                </button>
              </div>
            )}
          </div>

          <button className="footer-icon-button" onClick={handleSubmit}>
            <img src={send} alt="Send" className="footer-icon" />
          </button>
        </footer>
        <p className="disclaimer-desktop" style={{fontSize:"0.75rem", paddingTop:"1rem", color:"white"}}>
        All responses are AI generated. AI responses may be inaccurate. Use at your own discretion. See our 
  <a href="https://docs.ai.aitech.io/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color:"white", marginLeft: "4px", marginRight:"4px"}}>Terms of Service</a> 
  and 
  <a href="https://docs.ai.aitech.io/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color:"white", marginLeft: "4px", marginRight:"4px"}}>Privacy Policy</a>.
</p>
      </div>
    </div>
  );
};

export default ChatDesktop;
